import { useCallback, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';

import knowledgeApi from '@src/api/knowledgeBase';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { useMessage } from '@src/context/common';

const TABS = [
  { value: 'client', label: 'Client knowledge' },
  { value: 'company', label: 'Company knowledge' },
];

const defaultFormValues = {
  title: '',
  content: '',
  tags: '',
  recency: dayjs(),
  client: null,
  audience: '',
};

const toTagString = (tags) =>
  Array.isArray(tags) && tags.length ? tags.join(', ') : '';

const parseTags = (value) =>
  (value || '')
    .split(',')
    .map((tag) => tag.trim())
    .filter(Boolean);

const getResults = (payload) => {
  if (!payload) {
    return [];
  }
  if (Array.isArray(payload)) {
    return payload;
  }
  if (Array.isArray(payload.results)) {
    return payload.results;
  }
  if (Array.isArray(payload.data)) {
    return payload.data;
  }
  return [];
};

function KnowledgeBasePage() {
  const message = useMessage();
  const [activeTab, setActiveTab] = useState('client');

  const [clientDocs, setClientDocs] = useState([]);
  const [clientDocsLoading, setClientDocsLoading] = useState(false);
  const [clientFilter, setClientFilter] = useState(null);
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [clientSearch, setClientSearch] = useState('');

  const [companyDocs, setCompanyDocs] = useState([]);
  const [companyDocsLoading, setCompanyDocsLoading] = useState(false);
  const [companyAudience, setCompanyAudience] = useState('');
  const [companySearchInput, setCompanySearchInput] = useState('');
  const [companySearch, setCompanySearch] = useState('');

  const [dialogType, setDialogType] = useState(null);
  const [editingDoc, setEditingDoc] = useState(null);
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [submitting, setSubmitting] = useState(false);

  const {
    options: clientOptions,
    loading: clientOptionsLoading,
    handleInputChange: handleClientAutocompleteInput,
    addOption: addClientOption,
    findOptionByValue: findClientOptionByValue,
  } = useClientAutocomplete();

  useEffect(() => {
    const handle = setTimeout(() => {
      setClientSearch(clientSearchInput.trim());
    }, 300);

    return () => clearTimeout(handle);
  }, [clientSearchInput]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setCompanySearch(companySearchInput.trim());
    }, 300);

    return () => clearTimeout(handle);
  }, [companySearchInput]);

  const loadClientDocs = useCallback(() => {
    setClientDocsLoading(true);
    knowledgeApi
      .listClientDocuments({ client: clientFilter, search: clientSearch })
      .then((response) => {
        const docs = getResults(response?.data);
        setClientDocs(docs);
        docs.forEach((doc) => {
          if (doc?.client && doc?.client_name) {
            addClientOption({
              value: doc.client,
              label: doc.client_name,
            });
          }
        });
      })
      .catch((error) => {
        console.error('Failed to load client knowledge documents', error);
        message.error('Unable to load client documentation.');
      })
      .finally(() => setClientDocsLoading(false));
  }, [addClientOption, clientFilter, clientSearch, message]);

  const loadCompanyDocs = useCallback(() => {
    setCompanyDocsLoading(true);
    knowledgeApi
      .listCompanyDocuments({ audience: companyAudience, search: companySearch })
      .then((response) => {
        setCompanyDocs(getResults(response?.data));
      })
      .catch((error) => {
        console.error('Failed to load company knowledge documents', error);
        message.error('Unable to load company documentation.');
      })
      .finally(() => setCompanyDocsLoading(false));
  }, [companyAudience, companySearch, message]);

  useEffect(() => {
    loadClientDocs();
  }, [loadClientDocs]);

  useEffect(() => {
    loadCompanyDocs();
  }, [loadCompanyDocs]);

  const openDialog = useCallback(
    (type, doc = null) => {
      setDialogType(type);
      setEditingDoc(doc);
      if (doc) {
        setFormValues({
          title: doc.title || '',
          content: doc.content || '',
          tags: toTagString(doc.tags),
          recency: doc.recency ? dayjs(doc.recency) : dayjs(),
          client: type === 'client' ? doc.client ?? null : null,
          audience: type === 'company' ? doc.audience || '' : '',
        });
        if (type === 'client' && doc.client && doc.client_name) {
          addClientOption({
            value: doc.client,
            label: doc.client_name,
          });
        }
      } else {
        setFormValues({ ...defaultFormValues, recency: dayjs() });
      }
    },
    [addClientOption],
  );

  const closeDialog = useCallback(() => {
    setDialogType(null);
    setEditingDoc(null);
    setFormValues({ ...defaultFormValues, recency: dayjs() });
    setSubmitting(false);
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!dialogType) {
      return;
    }

    const payload = {
      title: formValues.title?.trim() || '',
      content: formValues.content || '',
      tags: parseTags(formValues.tags),
    };

    if (formValues.recency) {
      payload.recency = dayjs(formValues.recency).toISOString();
    }

    setSubmitting(true);

    if (dialogType === 'client') {
      if (!formValues.client) {
        message.error('Please select a client for this document.');
        setSubmitting(false);
        return;
      }
      payload.client = formValues.client;
      const request = editingDoc
        ? knowledgeApi.updateClientDocument(editingDoc.id, payload)
        : knowledgeApi.createClientDocument(payload);
      request
        .then(() => {
          message.success(
            editingDoc ? 'Client document updated.' : 'Client document created.',
          );
          closeDialog();
          loadClientDocs();
        })
        .catch((error) => {
          console.error('Failed to save client document', error);
          message.error('Could not save client document.');
          setSubmitting(false);
        });
      return;
    }

    if (dialogType === 'company') {
      payload.audience = formValues.audience?.trim() || '';
      const request = editingDoc
        ? knowledgeApi.updateCompanyDocument(editingDoc.id, payload)
        : knowledgeApi.createCompanyDocument(payload);
      request
        .then(() => {
          message.success(
            editingDoc
              ? 'Company document updated.'
              : 'Company document created.',
          );
          closeDialog();
          loadCompanyDocs();
        })
        .catch((error) => {
          console.error('Failed to save company document', error);
          message.error('Could not save company document.');
          setSubmitting(false);
        });
    }
  }, [
    closeDialog,
    dialogType,
    editingDoc,
    formValues,
    loadClientDocs,
    loadCompanyDocs,
    message,
  ]);

  const confirmAndDelete = useCallback(
    (type, doc) => {
      if (!doc?.id) {
        return;
      }
      const confirmed = window.confirm(
        `Delete “${doc.title}”? This action cannot be undone.`,
      );
      if (!confirmed) {
        return;
      }

      const request =
        type === 'client'
          ? knowledgeApi.deleteClientDocument(doc.id)
          : knowledgeApi.deleteCompanyDocument(doc.id);

      request
        .then(() => {
          message.success('Document deleted.');
          if (type === 'client') {
            loadClientDocs();
          } else {
            loadCompanyDocs();
          }
        })
        .catch((error) => {
          console.error('Failed to delete knowledge document', error);
          message.error('Could not delete document.');
        });
    },
    [loadClientDocs, loadCompanyDocs, message],
  );

  const clientRows = useMemo(() => clientDocs || [], [clientDocs]);
  const companyRows = useMemo(() => companyDocs || [], [companyDocs]);

  const dialogTitle = dialogType === 'client'
    ? editingDoc
      ? 'Edit client document'
      : 'Add client document'
    : editingDoc
    ? 'Edit company document'
    : 'Add company document';

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4">Knowledge Base</Typography>
      </Stack>

      <Tabs
        value={activeTab}
        onChange={(event, value) => setActiveTab(value)}
        sx={{ mb: 3 }}
      >
        {TABS.map((tab) => (
          <Tab key={tab.value} label={tab.label} value={tab.value} />
        ))}
      </Tabs>

      {activeTab === 'client' ? (
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <Autocomplete
                options={clientOptions}
                loading={clientOptionsLoading}
                onInputChange={handleClientAutocompleteInput}
                value={findClientOptionByValue(clientFilter)}
                onChange={(event, option) => setClientFilter(option ? option.value : null)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Filter by client"
                    placeholder="Start typing to search clients"
                  />
                )}
                sx={{ minWidth: { xs: '100%', md: 240 } }}
              />
              <TextField
                value={clientSearchInput}
                onChange={(event) => setClientSearchInput(event.target.value)}
                label="Search"
                placeholder="Search title or content"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openDialog('client')}
              >
                Add document
              </Button>
            </Stack>
          </CardContent>
          <Divider />
          {clientDocsLoading ? (
            <Box py={6} display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: { xs: '50%', md: '45%' } }}>Title</TableCell>
                    <TableCell>Client</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Recency</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientRows.length ? (
                    clientRows.map((doc) => (
                      <TableRow key={doc.id} hover>
                        <TableCell sx={{ width: { xs: '50%', md: '45%' }, maxWidth: 420 }}>
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle2">{doc.title}</Typography>
                            <Tooltip title={doc.content || ''} placement="top-start">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {doc.content || 'No content provided'}
                              </Typography>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell>{doc.client_name || '—'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {(doc.tags || []).length ? (
                              doc.tags.map((tag) => (
                                <Chip key={`${doc.id}-${tag}`} label={tag} size="small" />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                No tags
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {doc.recency ? dayjs(doc.recency).format('MMM D, YYYY h:mm A') : '—'}
                        </TableCell>
                        <TableCell>
                          {doc.updated_at
                            ? dayjs(doc.updated_at).format('MMM D, YYYY h:mm A')
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => openDialog('client', doc)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton color="error" onClick={() => confirmAndDelete('client', doc)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No client documentation found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
              <TextField
                label="Filter by audience"
                value={companyAudience}
                onChange={(event) => setCompanyAudience(event.target.value || '')}
                placeholder="e.g. sales, onboarding"
                sx={{ minWidth: { xs: '100%', md: 220 } }}
              />
              <TextField
                value={companySearchInput}
                onChange={(event) => setCompanySearchInput(event.target.value)}
                label="Search"
                placeholder="Search title or content"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                sx={{ flexGrow: 1 }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => openDialog('company')}
              >
                Add document
              </Button>
            </Stack>
          </CardContent>
          <Divider />
          {companyDocsLoading ? (
            <Box py={6} display="flex" justifyContent="center" alignItems="center">
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: { xs: '50%', md: '45%' } }}>Title</TableCell>
                    <TableCell>Audience</TableCell>
                    <TableCell>Tags</TableCell>
                    <TableCell>Recency</TableCell>
                    <TableCell>Updated</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companyRows.length ? (
                    companyRows.map((doc) => (
                      <TableRow key={doc.id} hover>
                        <TableCell sx={{ width: { xs: '50%', md: '45%' }, maxWidth: 420 }}>
                          <Stack spacing={0.5}>
                            <Typography variant="subtitle2">{doc.title}</Typography>
                            <Tooltip title={doc.content || ''} placement="top-start">
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                }}
                              >
                                {doc.content || 'No content provided'}
                              </Typography>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                        <TableCell>{doc.audience || '—'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {(doc.tags || []).length ? (
                              doc.tags.map((tag) => (
                                <Chip key={`${doc.id}-${tag}`} label={tag} size="small" />
                              ))
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                No tags
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {doc.recency ? dayjs(doc.recency).format('MMM D, YYYY h:mm A') : '—'}
                        </TableCell>
                        <TableCell>
                          {doc.updated_at
                            ? dayjs(doc.updated_at).format('MMM D, YYYY h:mm A')
                            : '—'}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton onClick={() => openDialog('company', doc)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                          <IconButton color="error" onClick={() => confirmAndDelete('company', doc)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No company documentation found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}

      <Dialog open={Boolean(dialogType)} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            {dialogType === 'client' ? (
              <Autocomplete
                options={clientOptions}
                loading={clientOptionsLoading}
                onInputChange={handleClientAutocompleteInput}
                value={findClientOptionByValue(formValues.client)}
                onChange={(event, option) =>
                  handleFormChange('client', option ? option.value : null)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Client"
                    placeholder="Start typing to search clients"
                    required
                  />
                )}
              />
            ) : (
              <TextField
                label="Audience"
                value={formValues.audience}
                onChange={(event) => handleFormChange('audience', event.target.value)}
                placeholder="Optional audience hint (e.g. sales, onboarding)"
              />
            )}
            <TextField
              label="Title"
              value={formValues.title}
              onChange={(event) => handleFormChange('title', event.target.value)}
              required
            />
            <TextField
              label="Content"
              value={formValues.content}
              onChange={(event) => handleFormChange('content', event.target.value)}
              multiline
              minRows={4}
            />
            <TextField
              label="Tags"
              value={formValues.tags}
              onChange={(event) => handleFormChange('tags', event.target.value)}
              helperText="Separate tags with commas"
            />
            <DateTimePicker
              label="Recency"
              value={formValues.recency}
              onChange={(value) => handleFormChange('recency', value)}
              slotProps={{
                textField: {
                  helperText: 'This date is used to prioritise the snippet in prompts.',
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default KnowledgeBasePage;
